import { fmtX } from "./format.js";
import { MEASURE_LABELS, isPartialPoint } from "./scope.js";
import { istToday } from "../../shared/dates.js";

/** Normalise any widget payload into { columns, rows } for table view and CSV. */
export function asTable(widget) {
  const d = widget.data;
  if (!d) return { columns: [], rows: [] };
  if (d.columns && d.rows) return d;
  if (d.series) {
    const today = istToday();
    return {
      columns: [{ key: "x", label: "Period" }, ...d.series.map((s) => ({ key: s.key, label: s.label, format: d.format }))],
      rows: d.x.map((x, i) => ({
        x: `${fmtX(x, d.xFormat)}${isPartialPoint(x, d.xFormat, today) ? " (partial)" : ""}`,
        ...Object.fromEntries(d.series.map((s) => [s.key, s.values[i]])),
      })),
    };
  }
  if (d.steps) {
    // Each step's share is of its parent step (a branch is a side split, not a stage).
    return {
      columns: [
        { key: "label", label: "Step" },
        { key: "value", label: "Users", format: "int" },
        { key: "pct", label: "% of parent step", format: "pct" },
        { key: "of", label: "Parent step" },
      ],
      rows: d.steps.map((st, i) => {
        const pi = st.parent ?? (i ? i - 1 : null);
        const p = pi !== null ? d.steps[pi] : null;
        return { label: st.branch ? `${st.label} (branch)` : st.label, value: st.value, pct: p?.value ? (st.value / p.value) * 100 : null, of: p?.label ?? "" };
      }),
    };
  }
  if (d.values && d.rows) {
    return {
      columns: [{ key: "day", label: "Day" }, ...d.cols.map((h) => ({ key: `h${h}`, label: `${h}:00`, format: d.format }))],
      rows: d.rows.map((day, ri) => ({ day, ...Object.fromEntries(d.cols.map((h, ci) => [`h${h}`, d.values[ri][ci]])) })),
    };
  }
  if (d.items && d.stack) {
    // Split bars: one column per segment after the total.
    return {
      columns: [
        { key: "label", label: "Label" },
        { key: "value", label: "Total", format: d.format || "int" },
        ...d.stack.keys.map((k) => ({ key: `part:${k.key}`, label: `${d.stack.title}: ${k.label}`, format: d.format || "int" })),
      ],
      rows: d.items.map((i) => ({ label: i.label, value: i.value, ...Object.fromEntries(d.stack.keys.map((k) => [`part:${k.key}`, i.parts?.[k.key] ?? 0])) })),
    };
  }
  if (d.items) {
    const kpi = widget.kind === "kpis";
    return {
      columns: [
        { key: "label", label: kpi ? "Metric" : "Label" },
        { key: "value", label: "Value", format: kpi ? undefined : d.format || "int" },
        ...(kpi ? [{ key: "prev", label: "Previous period" }] : []),
      ],
      rows: d.items.map((i) => ({
        label: kpi && i.state === "proxy" ? `${i.label} (proxy)` : i.label,
        // Not measured / not applicable is said in words: never a blank that reads as 0.
        value: i.state === "not_measured" || i.state === "not_applicable" ? MEASURE_LABELS[i.state] : i.value,
        prev: i.prev,
      })),
    };
  }
  return { columns: [], rows: [] };
}
